import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client/react'

import {
  Button,
  CopyIcon,
  Dialog,
  Flex,
  Form,
  IconButton,
  Input,
  Link,
  Notice,
  RadioCard,
  Switch,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import style from '../ProfileSettings.module.css'
import { INITIATE_UPDATE_EMAIL } from './BackupEmail'
import { DisableTFADialog } from './DisableTFADialog/DisableTFADialog'
import OtpQrCode from './OtpQrCode'
import { SettingsRow } from '../../shared/components/SettingsRow'
import { isValidEmail } from '../utils/isValidEmail'

export const TWO_FACTOR_SETUP = graphql(/* GraphQL */ `
  mutation TwoFactorSetup($input: TwoFactorSetupInput!) {
    twoFactorSetup(input: $input) {
      ... on TwoFactorSetupResponse {
        key
      }
      ... on InvalidInputError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const TWO_FACTOR_CONFIRM = graphql(/* GraphQL */ `
  mutation TwoFactorConfirm($input: TwoFactorConfirmInput!) {
    twoFactorConfirm(input: $input) {
      ... on TwoFactorConfirmResponse {
        recoveryCode
      }
      ... on InvalidInputError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const TWO_FACTOR_RECOVERY = graphql(/* GraphQL */ `
  query TwoFactorRecoveryCode($input: TwoFactorRecoveryCodeInput!) {
    recoveryCode(input: $input) {
      ... on TwoFactorRecoveryCodeResponse {
        recoveryCode
      }
      ... on InvalidInputError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const TWO_FACTOR_REQUEST_ONE_TIME_PASSWORD = graphql(/* GraphQL */ `
  mutation TwoFactorRequestOneTimePassword {
    twoFactorRequestOneTimePassword {
      ... on EmptySuccess {
        __typename
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

// Modal state
type TfaMethod = 'app' | 'email'

function isTfaMethod(value: string): value is TfaMethod {
  return value === 'app' || value === 'email'
}

enum ModalPage {
  ChooseType = 'chooseType',
  SetupApp = 'setupApp',
  SetupEmail = 'setupEmail',
  AddBackupEmail = 'addBackupEmail',
  ConfirmCode = 'confirmCode',
  ShowRecovery = 'showRecovery',
  // When the user clicks on "View Code" in the UI
  ConfirmCodeRecovery = 'confirmCodeForRecovery',
  // When the user clicks on "Edit Method" in the UI and we need to first confirm authentication
  ConfirmEditTFA = 'confirmEditTFA',
}

/**
 * Props for the standard Settings / Profile rendering of 2FA.
 *
 * When `forced` is true (e.g. the org-enforce-2FA gate sends a member here)
 * the component:
 *   - Skips the outer "two factor authentication" header/toggle — the
 *     hosting page (`/setup-2fa-required`) provides its own explanation and
 *     a "Set up 2FA" button.
 *   - Accepts controlled `open` / `onOpenChange` props so the hosting page
 *     decides when the setup dialog appears. The user can still close the
 *     dialog normally to return to the page; the page is the gate, not the
 *     dialog.
 *   - Replaces the ChooseType title/description with copy that mentions
 *     org enforcement.
 *   - On "Done" in the recovery-code step, calls `onSetupComplete()` so
 *     the page can refetch the account and navigate away from
 *     `/setup-2fa-required`.
 */
type TwoFactorProps = (
  | { onAddBackup: () => void; forced?: false; onSetupComplete?: undefined }
  | { onAddBackup?: () => void; forced: true; onSetupComplete: () => void }
) & {
  /** Controlled open state — when provided, the parent owns the dialog visibility. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * In-page notice on Settings > Profile that recommends enabling 2FA. Rendered
 * alongside the other profile banners when the account has no 2FA configured.
 * Dismissal is persisted per-account so it stays hidden across sessions.
 */
export const TwoFactorBanner = (): JSX.Element | null => {
  const { account } = useAccount()
  // Shares the `tfaBanner` dismissal key with the Settings menu 2FA badge, so
  // dismissing this notice also hides that badge.
  const banner = useDismissableBanner('tfaBanner')

  if (!account?.id || account.tfa || !banner.isActive) {
    return null
  }

  return (
    <Notice variant="info" onDismiss={banner.dismiss}>
      <Notice.Text>
        For added security and easier logins, we recommend setting up two-factor
        authentication.{' '}
        <Link
          external
          href="https://support.buffer.com/articles/using-two-factor-authentication-in-buffer-FClTl67lZB"
        >
          Learn more
        </Link>
      </Notice.Text>
    </Notice>
  )
}

const TwoFactor = (props: TwoFactorProps): JSX.Element => {
  const {
    onAddBackup,
    forced = false,
    onSetupComplete,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
  } = props
  const { account, refetchAccount } = useAccount()
  const accountEmail = account?.email
  const tfa = account?.tfa
  const showBackupEmailPrompt = !account?.backupEmail
  const showBackupEmailPromptRef = useRef(showBackupEmailPrompt)
  showBackupEmailPromptRef.current = showBackupEmailPrompt
  const onAddBackupRef = useRef(onAddBackup)
  onAddBackupRef.current = onAddBackup
  const recoveryRequestIdRef = useRef(0)

  const [modalPage, setModalPage] = useState<ModalPage>(ModalPage.ChooseType)
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false)
  /**
   * Proof that the account holds the two factor method it has now, which
   * `twoFactorConfirm` requires before it replaces that method. The
   * ConfirmEditTFA step earns it by verifying the current code, and the
   * recovery code it returns carries the proof to the final call. `reset()`
   * keeps it, so stepping Back inside the flow does not ask the user to verify
   * twice; closing the dialog drops it.
   */
  const [currentTwoFactorProof, setCurrentTwoFactorProof] = useState('')
  const isControlled = controlledOpen !== undefined
  const isModalOpen = isControlled ? controlledOpen : internalIsModalOpen
  const setIsModalOpen = (next: boolean): void => {
    if (!next) {
      setCurrentTwoFactorProof('')
    }
    if (isControlled) {
      controlledOnOpenChange?.(next)
    } else {
      setInternalIsModalOpen(next)
    }
  }
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false)

  // State
  /**
   * The method the dialog works with. The initial value is read once at mount,
   * before the account may have loaded, and a pick the user abandons stays
   * here. "Edit Method" and "View Code" verify the method in place now, so they
   * set this to `tfa.type` before they open the dialog.
   */
  const [tfaMethod, setTfaMethod] = useState<'app' | 'email'>(
    tfa?.type ?? 'app',
  )
  const [secret, setSecret] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [backupEmail, setBackupEmail] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [password, setPassword] = useState('')

  // GraphQL mutations
  const [setupTwoFactor, { data: setupData, loading: setupLoading }] =
    useMutation(TWO_FACTOR_SETUP)
  const [confirmTwoFactor, { data: confirmData, loading: confirmLoading }] =
    useMutation(TWO_FACTOR_CONFIRM)
  const [getRecoveryCode, { loading: recoveryLoading }] =
    useLazyQuery(TWO_FACTOR_RECOVERY)
  const [requestOneTimePassword] = useMutation(
    TWO_FACTOR_REQUEST_ONE_TIME_PASSWORD,
  )
  const [
    addBackupEmail,
    { data: backupEmailData, loading: backupEmailLoading },
  ] = useMutation(INITIATE_UPDATE_EMAIL)

  const [setupErrorMessage, setSetupErrorMessage] = useState<string | null>(
    null,
  )
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<string | null>(
    null,
  )
  const [backupEmailErrorMessage, setBackupEmailErrorMessage] = useState<
    string | null
  >(null)

  const loading =
    setupLoading || confirmLoading || recoveryLoading || backupEmailLoading

  function handleNext(): void {
    // Choose Type
    if (modalPage === ModalPage.ChooseType) {
      if (tfaMethod === 'app') {
        setModalPage(ModalPage.SetupApp)
      } else if (tfaMethod === 'email') {
        setModalPage(ModalPage.ConfirmCode)
        setupTwoFactor({
          variables: {
            input: {
              method: 'email',
            },
          },
        })
      }
    }
    // Setup App
    if (
      modalPage === ModalPage.SetupApp ||
      modalPage === ModalPage.SetupEmail
    ) {
      setModalPage(ModalPage.ConfirmCode)
    }

    // Confirm Code
    if (modalPage === ModalPage.ConfirmCode) {
      confirmTwoFactor({
        variables: {
          input: {
            method: tfaMethod,
            key: secret,
            code: confirmationCode,
            currentCode: currentTwoFactorProof || undefined,
          },
        },
      })
    }

    // Backup Email
    if (modalPage === ModalPage.AddBackupEmail && account) {
      if (!isValidEmail(backupEmail)) {
        setBackupEmailErrorMessage('Please enter a valid email address')
      } else if (!password) {
        setBackupEmailErrorMessage('Please enter your current password')
      } else {
        addBackupEmail({
          variables: {
            input: {
              accountId: account.id,
              password,
              isBackupEmail: true,
              newEmail: backupEmail,
            },
          },
        })
      }
    }

    // Get Recovery Code + Confirm Edit TFA
    if (
      modalPage === ModalPage.ConfirmCodeRecovery ||
      modalPage === ModalPage.ConfirmEditTFA
    ) {
      const requestedPage = modalPage
      const requestId = ++recoveryRequestIdRef.current
      void getRecoveryCode({
        variables: {
          input: {
            code: confirmationCode,
          },
        },
      })
        .then(({ data }) => {
          if (requestId !== recoveryRequestIdRef.current) return undefined

          const recoveryResult = data?.recoveryCode
          if (recoveryResult?.__typename === 'TwoFactorRecoveryCodeResponse') {
            setRecoveryCode(recoveryResult.recoveryCode)
            if (requestedPage === ModalPage.ConfirmEditTFA) {
              setCurrentTwoFactorProof(recoveryResult.recoveryCode)
            }
            setModalPage(
              requestedPage === ModalPage.ConfirmCodeRecovery
                ? ModalPage.ShowRecovery
                : ModalPage.ChooseType,
            )
          }

          if (
            recoveryResult?.__typename === 'UnexpectedError' ||
            recoveryResult?.__typename === 'InvalidInputError'
          ) {
            setConfirmErrorMessage(recoveryResult.message)
          }

          return undefined
        })
        .catch(() => {
          if (requestId !== recoveryRequestIdRef.current) return undefined

          setConfirmErrorMessage('Something went wrong. Please try again.')
          return undefined
        })
    }

    // Show Recovery
    if (modalPage === ModalPage.ShowRecovery) {
      reset()
      setIsModalOpen(false)
      if (forced) {
        // Hand control back to the forced-setup route so it can refetch
        // QUERY_ACCOUNT and navigate away from /setup-2fa-required.
        onSetupComplete?.()
      }
    }
  }

  // Trigger Fetches
  useEffect(() => {
    if (
      modalPage === ModalPage.SetupApp ||
      modalPage === ModalPage.SetupEmail
    ) {
      setupTwoFactor({
        variables: {
          input: {
            method: tfaMethod,
          },
        },
      })
    }
    if (modalPage === ModalPage.ConfirmCodeRecovery) {
      requestOneTimePassword()
    }
    // If the users current TFA method is email or SMS we need to request a one-time password
    // App TFA method will already be available to the user
    if (modalPage === ModalPage.ConfirmEditTFA && tfaMethod !== 'app') {
      requestOneTimePassword()
    }
  }, [modalPage, setupTwoFactor, tfaMethod, requestOneTimePassword])

  // Handle twoFactorSetup response
  useEffect(() => {
    setSetupErrorMessage(null)
    const setupResult = setupData?.twoFactorSetup
    if (setupResult?.__typename === 'TwoFactorSetupResponse') {
      setSecret(setupResult.key ?? '')
    }
    if (
      setupResult?.__typename === 'UnexpectedError' ||
      setupResult?.__typename === 'InvalidInputError'
    ) {
      reset()
      // Temporary hack to display rate limiting.
      const responseMessage = setupResult.message || ''
      const toastMessage = responseMessage.includes('attempts')
        ? 'Too many attempts. Please try again in few minutes'
        : 'An error occurred while setting up two factor authentication'
      toast.error(toastMessage)
    }
  }, [setupData])

  // Handle twoFactorConfirm response
  useEffect(() => {
    const confirmResult = confirmData?.twoFactorConfirm
    if (confirmResult?.__typename === 'TwoFactorConfirmResponse') {
      setRecoveryCode(confirmResult.recoveryCode)
      if (showBackupEmailPromptRef.current) {
        setModalPage(ModalPage.AddBackupEmail)
      } else {
        setModalPage(ModalPage.ShowRecovery)
      }
      if (refetchAccount) {
        refetchAccount()
      }
    }
    if (
      confirmResult?.__typename === 'UnexpectedError' ||
      confirmResult?.__typename === 'InvalidInputError'
    ) {
      setConfirmErrorMessage(confirmResult.message)
    }
  }, [confirmData, refetchAccount])

  // Handle addBackupEmail response
  useEffect(() => {
    if (backupEmailData) {
      if (backupEmailData?.initiateUpdateEmail?.__typename !== 'EmptySuccess') {
        setBackupEmailErrorMessage('Error adding backup email. Try again later')
      } else {
        setBackupEmailErrorMessage(null)
        toast.success(
          'Email sent! Head to your inbox and click the verification link',
        )
        setModalPage(ModalPage.ShowRecovery)
        onAddBackupRef.current?.()
      }
    }
  }, [backupEmailData])

  function reset(): void {
    recoveryRequestIdRef.current += 1
    setModalPage(ModalPage.ChooseType)
    // reset state
    setSecret('')
    setConfirmationCode('')
    setRecoveryCode('')
    setPassword('')
    setSetupErrorMessage(null)
    setConfirmErrorMessage(null)
  }

  const methodNameMap = {
    app: 'Authenticator App',
    sms: 'SMS',
    email: 'Email',
  }

  return (
    <>
      {!forced && (
        <Flex gap="lg" direction="column" className={style.fullWidth}>
          <SettingsRow id="two-factor">
            <SettingsRow.Header>
              <SettingsRow.Title>Two Factor Authentication</SettingsRow.Title>
              <SettingsRow.Description>
                Two factor authentication adds an extra layer of security for
                your Buffer account. Whenever you log in to your account, after
                entering your username and password, you will be asked for a
                second authentication code that will be sent to you via email or
                generated on an authentication app, such as Google Authenticator
                or Authy.{' '}
                <Link
                  external
                  href="https://support.buffer.com/articles/using-two-factor-authentication-in-buffer-FClTl67lZB?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
                >
                  Learn more
                </Link>
              </SettingsRow.Description>
            </SettingsRow.Header>
            <SettingsRow.Control inline>
              {(ids) => (
                <Switch
                  id="two-factor"
                  aria-labelledby={ids.headingId}
                  aria-describedby={ids.descriptionId}
                  checked={!!tfa}
                  onClick={(e): void => {
                    e.preventDefault()
                    if (tfa) {
                      setIsDisableDialogOpen(true)
                    } else {
                      setIsModalOpen(true)
                    }
                  }}
                />
              )}
            </SettingsRow.Control>
          </SettingsRow>
          {!!tfa && (
            <>
              <SettingsRow id="two-factor-method">
                <SettingsRow.Header>
                  <SettingsRow.Title as="h3" size="xs">
                    Method: {tfa ? methodNameMap[tfa.type] : 'Disabled'}
                  </SettingsRow.Title>
                  <SettingsRow.Description>
                    {tfa.type === 'app' &&
                      'Use an authenticator app to generate a one-time code'}
                    {tfa.type === 'email' &&
                      'Receive an email with one-time code'}
                  </SettingsRow.Description>
                </SettingsRow.Header>
                <SettingsRow.Control>
                  {(ids) => (
                    <Button
                      size="medium"
                      variant="secondary"
                      aria-describedby={ids.descriptionId}
                      onClick={(e): void => {
                        e.preventDefault()
                        setTfaMethod(tfa.type)
                        setModalPage(ModalPage.ConfirmEditTFA)
                        setIsModalOpen(true)
                      }}
                    >
                      Edit Method
                    </Button>
                  )}
                </SettingsRow.Control>
              </SettingsRow>
              <SettingsRow id="two-factor-recovery-code">
                <SettingsRow.Header>
                  <SettingsRow.Title as="h3" size="xs">
                    Recovery Code
                  </SettingsRow.Title>
                  <SettingsRow.Description>
                    If you lose access to your {methodNameMap[tfa.type]}, use
                    this code to log in to your account.
                  </SettingsRow.Description>
                </SettingsRow.Header>
                <SettingsRow.Control>
                  {(ids) => (
                    <Button
                      size="medium"
                      variant="secondary"
                      aria-describedby={ids.descriptionId}
                      onClick={(e): void => {
                        e.preventDefault()
                        setTfaMethod(tfa.type)
                        setModalPage(ModalPage.ConfirmCodeRecovery)
                        setIsModalOpen(true)
                      }}
                    >
                      View Code
                    </Button>
                  )}
                </SettingsRow.Control>
              </SettingsRow>
            </>
          )}
        </Flex>
      )}

      {/* 2FA Setup Modal */}

      <Dialog
        modal
        open={isModalOpen}
        onOpenChange={(isOpen): void => {
          if (!isOpen) {
            setIsModalOpen(false)
            reset()
          }
        }}
      >
        <Dialog.Content className={style.twoFactorDialog}>
          {modalPage === ModalPage.ChooseType && (
            <>
              <Dialog.Header>
                <Dialog.Title>
                  {forced
                    ? 'Set Up Two-Factor Authentication to Continue'
                    : 'Enable Two Factor Authentication'}
                </Dialog.Title>
                <Dialog.Description>
                  {forced
                    ? 'Your organization requires every team member to have two-factor authentication enabled before using Buffer. Set it up below to continue — you’ll only need to do this once.'
                    : 'How would you like us to send your security codes?'}
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body className={style.twoFactorDialogBody}>
                <RadioCard.Group
                  onValueChange={(value): void => {
                    if (isTfaMethod(value)) setTfaMethod(value)
                  }}
                  style={{
                    padding: 0,
                    margin: 0,
                  }}
                  defaultValue={tfaMethod}
                >
                  <RadioCard value="app">
                    <Flex gap="2xs" direction="column">
                      <Text weight="medium">Authenticator App</Text>
                      <Text color="subtle">
                        Use an authenticator app to generate a one-time code
                      </Text>
                    </Flex>
                  </RadioCard>
                  <RadioCard value="email">
                    <Flex gap="2xs" direction="column">
                      <Text weight="medium">Email</Text>
                      <Text color="subtle">
                        Receive an email with one-time code
                      </Text>
                    </Flex>
                  </RadioCard>
                </RadioCard.Group>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="tertiary"
                  onClick={(): void => {
                    reset()
                    setIsModalOpen(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Continue'}
                </Button>
              </Dialog.Footer>
            </>
          )}
          {modalPage === ModalPage.SetupApp && (
            <>
              <Dialog.Header>
                <Dialog.Title>Set up your authenticator app</Dialog.Title>
                <Dialog.Description>
                  Scan the QR code below in your authenticator app and
                  you&apos;re all set! (If you have not installed an
                  authenticator app yet,{' '}
                  <Link
                    external
                    href="https://support.buffer.com/articles/using-two-factor-authentication-in-buffer-FClTl67lZB?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
                  >
                    read here for more information.
                  </Link>
                  )
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body className={style.twoFactorDialogBody}>
                <OtpQrCode account={accountEmail ?? ''} secret={secret} />
                {setupErrorMessage && (
                  <Notice variant="error">{setupErrorMessage}</Notice>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="tertiary"
                  onClick={(): void => reset()}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Continue'}
                </Button>
              </Dialog.Footer>
              <Dialog.CloseButton />
            </>
          )}
          {(modalPage === ModalPage.ConfirmCode ||
            modalPage === ModalPage.ConfirmCodeRecovery ||
            modalPage === ModalPage.ConfirmEditTFA) && (
            <>
              <Dialog.Header>
                <Dialog.Title>Enter confirmation code</Dialog.Title>
                <Dialog.Description>
                  {modalPage === ModalPage.ConfirmEditTFA && (
                    <>
                      Before you can edit your method, we need to first
                      authenticate you. <br />
                      <br />
                    </>
                  )}
                  {modalPage !== ModalPage.ConfirmCodeRecovery &&
                    modalPage !== ModalPage.ConfirmEditTFA && (
                      <>Awesome! Now we just need to confirm everything. </>
                    )}
                  {tfaMethod === 'app' &&
                    'Open your authenticator app and input the generated code.'}
                  {tfaMethod === 'email' &&
                    'We just sent an email to your email address. Please enter the code below.'}
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body className={style.twoFactorDialogBody}>
                <Form name="codeForm">
                  <Form.Field name="code">
                    <Form.Label>Code</Form.Label>
                    <Form.Control>
                      <Input
                        autoComplete="one-time-code"
                        placeholder="000000"
                        onChange={(event): void =>
                          setConfirmationCode(event.target.value)
                        }
                      />
                    </Form.Control>
                    {confirmErrorMessage && (
                      <Notice
                        variant="error"
                        style={{ marginTop: 'var(--space-md)' }}
                      >
                        {confirmErrorMessage}
                      </Notice>
                    )}
                  </Form.Field>
                </Form>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="tertiary"
                  onClick={(): void => {
                    if (
                      modalPage === ModalPage.ConfirmCodeRecovery ||
                      modalPage === ModalPage.ConfirmEditTFA
                    ) {
                      setIsModalOpen(false)
                    }
                    if (tfaMethod === 'email') {
                      setModalPage(ModalPage.ChooseType)
                    } else {
                      setModalPage(ModalPage.SetupApp)
                    }
                  }}
                  disabled={loading}
                >
                  {modalPage === ModalPage.ConfirmCodeRecovery
                    ? 'Cancel'
                    : 'Back'}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Confirm'}
                </Button>
              </Dialog.Footer>
              <Dialog.CloseButton />
            </>
          )}
          {modalPage === ModalPage.AddBackupEmail && (
            <>
              <Dialog.Header>
                <Dialog.Title>Add backup email address</Dialog.Title>
                <Dialog.Description>
                  <>
                    Awesome! Your backup email address will be used as an
                    additional destination for security-relevant account
                    notifications.
                  </>
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body className={style.twoFactorDialogBody}>
                <Form name="backupEmailForm">
                  <Form.Field name="backupEmail">
                    <Form.Label>Backup Email</Form.Label>
                    <Form.Control>
                      <Input
                        autoComplete="email"
                        placeholder=""
                        onChange={(event): void =>
                          setBackupEmail(event.target.value)
                        }
                      />
                    </Form.Control>
                  </Form.Field>
                  <Form.Field
                    name="password"
                    style={{ marginTop: 'var(--space-md)' }}
                  >
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        placeholder="Enter your current password"
                        onChange={(e): void => setPassword(e.target.value)}
                        required
                      />
                    </Form.Control>
                  </Form.Field>
                  {backupEmailErrorMessage && (
                    <Notice
                      variant="error"
                      style={{ marginTop: 'var(--space-md)' }}
                    >
                      {backupEmailErrorMessage}
                    </Notice>
                  )}
                </Form>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="tertiary"
                  onClick={(): void => {
                    setModalPage(ModalPage.ShowRecovery)
                  }}
                  disabled={loading}
                >
                  Add later
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Confirm'}
                </Button>
              </Dialog.Footer>
              <Dialog.CloseButton />
            </>
          )}
          {modalPage === ModalPage.ShowRecovery && (
            <>
              <Dialog.Header>
                <Dialog.Title>Save this one-time recovery code</Dialog.Title>
                <Dialog.Description>
                  {tfaMethod === 'app' &&
                    "If you lose access to your authenticator app there's a possibility you could get locked out of your account. Save this code in a safe place to use if you can't log in with your authenticator app."}
                  {tfaMethod === 'email' &&
                    "If you lose access to your email there's a possibility you could get locked out of your account. Save this code in a safe place to use if you can't log in with your email."}
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body className={style.twoFactorDialogBody}>
                <Flex gap="xs" direction="row">
                  <Text className={style.tfaRecoveryCody}>{recoveryCode}</Text>
                  <IconButton
                    label="Copy recovery code"
                    variant="secondary"
                    onClick={(): void => {
                      navigator.clipboard.writeText(recoveryCode)
                      toast.success('Recovery code copied to clipboard')
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                </Flex>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="primary" onClick={handleNext}>
                  Done
                </Button>
              </Dialog.Footer>
              <Dialog.CloseButton />
            </>
          )}
        </Dialog.Content>
      </Dialog>

      {!forced && (
        <DisableTFADialog
          open={isDisableDialogOpen}
          onOpenChange={setIsDisableDialogOpen}
          onSuccess={refetchAccount}
        />
      )}
    </>
  )
}

export default TwoFactor
